// src/api/declarationNcs.api.ts — SCE Issues
// Déclaration NC-S (05/10) : le formulaire rempli est DÉPOSÉ comme un nouveau
// fichier dans un dossier SharePoint du site OP_SCE — un fichier par
// déclaration, nommé « NC-S <Composant> <NVS> JJMMAA ». Le flow 5 (« fichier
// créé ») l'envoie à la qualité. Règles et remplissage : utils/declarationNcs.ts.
//
// Le nom ne dit pas de quel problème il s'agit : l'ID est écrit dans la
// colonne Titre du fichier (et dans ses propriétés de document, que
// SharePoint reprend aussi dans Titre). C'est là que le flow 5 le lit.
//
// Le modèle est le classeur vierge « Déclaration réclamation fournisseur
// (NC-S) », rangé par Guillaume DANS le dossier des déclarations (05/10) : il
// est LU, jamais modifié. Son nom ne commence pas par « NC-S » : le flow 5
// l'ignore. On ne remplace
// donc aucun fichier existant — rien à verrouiller, rien à écraser
// (`conflictBehavior=fail` : un nom pris est refusé, on en prend un autre).
//
// Téléchargement par `@microsoft.graph.downloadUrl` et non `/content` : la
// redirection 302 de `/content` est refusée par le navigateur dès qu'il y a un
// en-tête Authorization (préflight CORS). L'adresse fournie est pré-authentifiée.

import { GraphApiError, graphFetch, graphGet, graphPatch } from './graphClient'
import { colonneSaisiPar } from '@/auth/comptePartage'
import {
  lireClasseurNcs,
  nomFichierNcs,
  remplirFormulaireNcs,
  type DeclarationNcs,
} from '@/utils/declarationNcs'

const SITE_ID = import.meta.env.VITE_SHAREPOINT_SITE_ID ?? ''

/** Le dossier des déclarations (bibliothèque « Documents » du site OP_SCE). */
export const DOSSIER_NCS = 'OPERATIONS/05-Fabrication Process/Déclarations NC-S'

/** Le formulaire vierge, rangé dans le dossier des déclarations. */
export const CHEMIN_MODELE_NCS = `${DOSSIER_NCS}/Déclaration réclamation fournisseur (NC-S).xlsx`

const TYPE_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

/** Nouvelles tentatives quand le nom choisi vient d'être pris. */
const ESSAIS = 3

interface ElementFichier {
  id: string
  name: string
  webUrl: string
  '@microsoft.graph.downloadUrl'?: string
}

export interface ModeleNcs {
  octets: ArrayBuffer
  /** Valeurs de la liste « Initiateur NC », telles qu'écrites. */
  personnes: string[]
}

export interface ResultatDeclarationNcs {
  /** Nom du fichier déposé. */
  fichier: string
  /** Ouvre le fichier (Excel pour le web). */
  lien: string
}

/** `/sites/…/drive/root:/OPERATIONS/…` — chaque segment encodé. */
function adresse(chemin: string): string {
  return `/sites/${SITE_ID}/drive/root:/${chemin.split('/').map(encodeURIComponent).join('/')}`
}

/**
 * Colonne Titre du fichier = ID du problème. SharePoint la remplit souvent
 * seul depuis les propriétés du document ; on l'écrit quand même, sans
 * bloquer : le fichier est déposé, c'est l'essentiel.
 */
async function ecrireTitre(itemId: string, issueId: string): Promise<void> {
  try {
    await graphPatch(`/sites/${SITE_ID}/drive/items/${itemId}/listItem/fields`, { Title: issueId })
  } catch (err) {
    console.warn('[declarationNcs] colonne Titre non écrite :', err)
  }
}

/**
 * Tablette partagée (07/10) : « Créé par » est le compte de la tablette, d'où
 * la colonne SaisiPar du fichier, que le flow 5 lit pour mettre en copie la
 * BONNE personne. Requête à part, après le Titre : si la colonne manque, le
 * Titre (l'ID que lit le flow) est déjà écrit.
 */
async function ecrireSaisiPar(itemId: string): Promise<void> {
  const saisi = colonneSaisiPar()
  if (!saisi.SaisiPar) return
  try {
    await graphPatch(`/sites/${SITE_ID}/drive/items/${itemId}/listItem/fields`, saisi)
  } catch (err) {
    console.warn('[declarationNcs] colonne SaisiPar non écrite :', err)
  }
}

/** Message lisible pour les erreurs attendues. */
export function messageErreurNcs(err: unknown): string {
  const status = err instanceof GraphApiError ? err.status : undefined
  if (status === 404) return `Formulaire NC-S vierge introuvable sur SharePoint (${CHEMIN_MODELE_NCS}).`
  if (status === 403) return "Pas le droit d'écrire dans le dossier des déclarations NC-S."
  if (err instanceof Error && /Classeur NC-S|Modèle modifié/.test(err.message)) return err.message
  return "La déclaration NC-S n'a pas pu être déposée. Réessayez."
}

/** Le formulaire vierge et sa liste de personnes. */
export async function lireModeleNcs(): Promise<ModeleNcs> {
  const element = await graphGet<ElementFichier>(adresse(CHEMIN_MODELE_NCS))
  const url = element['@microsoft.graph.downloadUrl']
  if (!url) throw new GraphApiError('Téléchargement du formulaire NC-S refusé', 403)
  const reponse = await fetch(url)
  if (!reponse.ok) throw new GraphApiError(`Téléchargement du formulaire NC-S (${reponse.status})`, reponse.status)
  const octets = await reponse.arrayBuffer()
  return { octets, personnes: (await lireClasseurNcs(octets)).personnes }
}

/** Noms des fichiers du dossier des déclarations ; vide s'il n'existe pas encore. */
export async function listerDeclarationsNcs(): Promise<string[]> {
  const noms: string[] = []
  let page: string | undefined = `${adresse(DOSSIER_NCS)}:/children?$select=name&$top=999`
  try {
    while (page) {
      const r: { value: { name: string }[]; '@odata.nextLink'?: string } = await graphGet(page)
      noms.push(...r.value.map((e) => e.name))
      page = r['@odata.nextLink']
    }
  } catch (err) {
    if (err instanceof GraphApiError && err.status === 404) return []
    throw err
  }
  return noms
}

/**
 * Remplit le formulaire et le dépose dans le dossier des déclarations, sous
 * un nom libre (`NC-S Vanne DN50 NVS12345 051026.xlsx`, puis « (2) »…), puis
 * écrit l'ID du problème dans la colonne Titre du fichier.
 * @param modele déjà lu à l'ouverture du formulaire ; relu sinon.
 */
export async function declarerNcs(
  issueId: string,
  declaration: DeclarationNcs,
  modele?: ModeleNcs,
): Promise<ResultatDeclarationNcs> {
  const octets = await remplirFormulaireNcs((modele ?? (await lireModeleNcs())).octets, declaration, issueId)
  for (let essai = 1; ; essai++) {
    const fichier = nomFichierNcs(declaration, await listerDeclarationsNcs())
    try {
      const cree = await graphFetch<ElementFichier>(
        `${adresse(`${DOSSIER_NCS}/${fichier}`)}:/content?@microsoft.graph.conflictBehavior=fail`,
        { method: 'PUT', rawBody: octets, headers: { 'Content-Type': TYPE_XLSX } },
      )
      await ecrireTitre(cree.id, issueId)
      await ecrireSaisiPar(cree.id)
      return { fichier: cree.name || fichier, lien: cree.webUrl }
    } catch (err) {
      // 409 : un collègue vient de déposer un fichier du même nom.
      const pris = err instanceof GraphApiError && err.status === 409
      if (!pris || essai >= ESSAIS) throw err
    }
  }
}
